import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
    Plane, Plus, RefreshCw, Clock, Users, CheckCircle, AlertTriangle,
    Play, Timer, UserCheck, Clipboard, ChevronRight, CircleDot
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
    pending: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    assigned: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    in_progress: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    completed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
};
const statusLabels = {
    pending: 'Pendiente', assigned: 'Asignado', in_progress: 'En Progreso', completed: 'Completado',
};

function TurnaroundCard({ t, onRefresh, isLeader, leaders }) {
    const [assigning, setAssigning] = useState(false);
    const [selectedLeader, setSelectedLeader] = useState('');
    const [showChecklist, setShowChecklist] = useState(false);
    const [teamDialog, setTeamDialog] = useState(false);
    const [teamSize, setTeamSize] = useState(t.team_size || 0);
    const [teamNames, setTeamNames] = useState((t.team_members || []).join(', '));

    const assign = async () => {
        if (!selectedLeader) return;
        try {
            await axios.patch(`${API}/gh/turnarounds/${t.id}/assign`, { leader_id: selectedLeader });
            toast.success('Turnaround asignado');
            setAssigning(false);
            onRefresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const startTurnaround = async () => {
        try {
            await axios.patch(`${API}/gh/turnarounds/${t.id}/start`);
            toast.success('Turnaround iniciado');
            onRefresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const updateService = async (key, action) => {
        try {
            await axios.patch(`${API}/gh/turnarounds/${t.id}/checklist/${key}`, { action });
            onRefresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const saveTeam = async () => {
        try {
            const members = teamNames.split(',').map(n => n.trim()).filter(Boolean);
            await axios.patch(`${API}/gh/turnarounds/${t.id}/team`, { team_size: teamSize, team_members: members });
            toast.success('Equipo registrado');
            setTeamDialog(false);
            onRefresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const completeTurnaround = async () => {
        try {
            await axios.patch(`${API}/gh/turnarounds/${t.id}/complete`, {});
            toast.success('Turnaround completado');
            onRefresh();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const completedCount = (t.checklist || []).filter(s => s.status === 'completed').length;
    const totalServices = (t.checklist || []).length;
    const pct = totalServices > 0 ? Math.round(completedCount / totalServices * 100) : 0;
    const elapsed = t.started_at ? Math.round((Date.now() - new Date(t.started_at).getTime()) / 60000) : null;

    return (
        <Card className={`card-aviation ${t.status === 'in_progress' ? 'border-amber-500/40' : ''}`} data-testid={`turnaround-${t.id}`}>
            <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-lg text-primary" data-testid="ta-flight">{t.flight_number}</span>
                            <Badge className={`${statusColors[t.status]} text-[10px]`}>{statusLabels[t.status]}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{t.airline_client} | {t.aircraft_registration} ({t.aircraft_type})</p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                        <p>Gate: <span className="font-mono font-bold">{t.gate || '-'}</span></p>
                        <p>Stand: <span className="font-mono font-bold">{t.stand || '-'}</span></p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                    <div className="bg-muted/40 rounded px-2 py-1.5 text-center">
                        <p className="text-muted-foreground">Llegada</p>
                        <p className="font-mono font-bold">{t.arrival_time ? new Date(t.arrival_time).toLocaleTimeString('es', {hour:'2-digit', minute:'2-digit'}) : '-'}</p>
                    </div>
                    <div className="bg-muted/40 rounded px-2 py-1.5 text-center">
                        <p className="text-muted-foreground">Salida</p>
                        <p className="font-mono font-bold">{t.departure_time ? new Date(t.departure_time).toLocaleTimeString('es', {hour:'2-digit', minute:'2-digit'}) : '-'}</p>
                    </div>
                    <div className="bg-muted/40 rounded px-2 py-1.5 text-center">
                        <p className="text-muted-foreground">{t.status === 'completed' ? 'Total' : 'Transcurrido'}</p>
                        <p className="font-mono font-bold">{t.total_time_minutes ? `${t.total_time_minutes} min` : elapsed !== null ? `${elapsed} min` : '-'}</p>
                    </div>
                </div>

                {/* Progress bar */}
                {t.status !== 'pending' && (
                    <div className="mb-3">
                        <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                            <span>Progreso</span>
                            <span className="font-mono">{completedCount}/{totalServices} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{width: `${pct}%`}} />
                        </div>
                    </div>
                )}

                {/* Team info */}
                {t.assigned_leader_name && (
                    <div className="flex items-center gap-2 text-xs mb-3">
                        <UserCheck className="w-3.5 h-3.5 text-primary" />
                        <span>Lider: <strong>{t.assigned_leader_name}</strong></span>
                        {t.team_size > 0 && <span className="text-muted-foreground">| {t.team_size} agentes</span>}
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                    {!isLeader && t.status === 'pending' && (
                        assigning ? (
                            <div className="flex items-center gap-2 w-full">
                                <Select value={selectedLeader} onValueChange={setSelectedLeader}>
                                    <SelectTrigger className="flex-1 h-8 text-xs" data-testid="assign-leader-select"><SelectValue placeholder="Seleccionar lider" /></SelectTrigger>
                                    <SelectContent>
                                        {leaders.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Button size="sm" className="h-8 text-xs" onClick={assign} data-testid="confirm-assign">Asignar</Button>
                                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setAssigning(false)}>X</Button>
                            </div>
                        ) : (
                            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setAssigning(true)} data-testid="assign-btn">
                                <UserCheck className="w-3.5 h-3.5 mr-1" />Asignar Lider
                            </Button>
                        )
                    )}
                    {(t.status === 'assigned' || t.status === 'in_progress') && (
                        <>
                            {t.status === 'assigned' && (
                                <Button size="sm" className="text-xs h-8 bg-amber-600 hover:bg-amber-700" onClick={startTurnaround} data-testid="start-ta-btn">
                                    <Play className="w-3.5 h-3.5 mr-1" />Iniciar
                                </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setTeamDialog(true)} data-testid="team-btn">
                                <Users className="w-3.5 h-3.5 mr-1" />Equipo ({t.team_size || 0})
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowChecklist(!showChecklist)} data-testid="checklist-btn">
                                <Clipboard className="w-3.5 h-3.5 mr-1" />Checklist
                            </Button>
                            {t.status === 'in_progress' && (
                                <Button size="sm" variant="outline" className="text-xs h-8 text-emerald-500 border-emerald-500/30" onClick={completeTurnaround} data-testid="complete-ta-btn">
                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />Completar
                                </Button>
                            )}
                        </>
                    )}
                    {t.status === 'completed' && (
                        <Button size="sm" variant="ghost" className="text-xs h-8" onClick={() => setShowChecklist(!showChecklist)}>
                            <Clipboard className="w-3.5 h-3.5 mr-1" />Ver Checklist
                        </Button>
                    )}
                </div>

                {/* Checklist */}
                {showChecklist && (
                    <div className="mt-3 border-t border-border/50 pt-3 space-y-1" data-testid="checklist-panel">
                        {(t.checklist || []).sort((a, b) => a.order - b.order).map(svc => (
                            <div key={svc.key} className={`flex items-center justify-between py-1.5 px-2 rounded text-xs ${svc.status === 'completed' ? 'bg-emerald-500/5' : svc.status === 'in_progress' ? 'bg-amber-500/5' : 'bg-muted/30'}`} data-testid={`svc-${svc.key}`}>
                                <div className="flex items-center gap-2 flex-1">
                                    {svc.status === 'completed' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> :
                                     svc.status === 'in_progress' ? <CircleDot className="w-4 h-4 text-amber-500" /> :
                                     <div className="w-4 h-4 rounded-full border border-muted-foreground/30" />}
                                    <span className={svc.status === 'completed' ? 'line-through text-muted-foreground' : ''}>{svc.label}</span>
                                    {svc.completed_at && <span className="text-[10px] text-muted-foreground ml-auto mr-2">{new Date(svc.completed_at).toLocaleTimeString('es', {hour:'2-digit', minute:'2-digit', second:'2-digit'})}</span>}
                                </div>
                                {t.status === 'in_progress' && svc.status !== 'completed' && (
                                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => updateService(svc.key, svc.status === 'pending' ? 'complete' : 'complete')} data-testid={`complete-svc-${svc.key}`}>
                                        <CheckCircle className="w-3 h-3" />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Team Dialog */}
                <Dialog open={teamDialog} onOpenChange={setTeamDialog}>
                    <DialogContent className="sm:max-w-[400px]">
                        <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] uppercase">Registrar Equipo</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label>Cantidad de Agentes</Label>
                                <Input type="number" min={1} max={20} value={teamSize} onChange={e => setTeamSize(parseInt(e.target.value) || 0)} data-testid="team-size-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Nombres (opcional, separados por coma)</Label>
                                <Textarea value={teamNames} onChange={e => setTeamNames(e.target.value)} placeholder="Pedro Lopez, Ana Garcia..." rows={2} data-testid="team-names-input" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setTeamDialog(false)}>Cancelar</Button>
                            <Button onClick={saveTeam} className="btn-primary" data-testid="save-team-btn">Guardar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
}

export default function GHDashboardPage() {
    const { user, hasRole } = useAuth();
    const [dashboard, setDashboard] = useState(null);
    const [turnarounds, setTurnarounds] = useState([]);
    const [leaders, setLeaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialog, setCreateDialog] = useState(false);
    const [activeTab, setActiveTab] = useState('active');
    const [newTa, setNewTa] = useState({
        airline_client: '', flight_number: '', aircraft_registration: '', aircraft_type: '',
        gate: '', stand: '', arrival_time: '', departure_time: '', notes: ''
    });
    const isSupervisor = hasRole('gh_supervisor') || hasRole('admin');

    const fetchAll = useCallback(async () => {
        try {
            const [dashRes, taRes, ldRes] = await Promise.all([
                axios.get(`${API}/gh/dashboard`),
                axios.get(`${API}/gh/turnarounds`),
                isSupervisor ? axios.get(`${API}/gh/leaders`).catch(() => ({data:[]})) : Promise.resolve({data: []}),
            ]);
            setDashboard(dashRes.data);
            setTurnarounds(taRes.data);
            setLeaders(ldRes.data.map(l => ({...l, name: `${l.first_name} ${l.last_name}`})));
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    }, [isSupervisor]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const createTurnaround = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/gh/turnarounds`, newTa);
            toast.success('Turnaround creado');
            setCreateDialog(false);
            setNewTa({ airline_client: '', flight_number: '', aircraft_registration: '', aircraft_type: '', gate: '', stand: '', arrival_time: '', departure_time: '', notes: '' });
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    };

    const kpis = dashboard?.kpis || {};
    const activeTas = turnarounds.filter(t => ['pending', 'assigned', 'in_progress'].includes(t.status));
    const completedTas = turnarounds.filter(t => t.status === 'completed');

    if (loading) {
        return <div className="space-y-4 animate-fade-in">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-lg skeleton-pulse" />)}</div>;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="gh-dashboard">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Ground Handling</h1>
                    <p className="text-muted-foreground">Centro de control de asistencia en tierra</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={fetchAll} data-testid="refresh-gh"><RefreshCw className="w-4 h-4 mr-2" />Actualizar</Button>
                    {isSupervisor && (
                        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary" data-testid="new-turnaround-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Turnaround</Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[500px]">
                                <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Crear Turnaround</DialogTitle></DialogHeader>
                                <form onSubmit={createTurnaround} className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Aerolinea Cliente</Label>
                                            <Input required value={newTa.airline_client} onChange={e => setNewTa({...newTa, airline_client: e.target.value})} placeholder="AeroDemo Airlines" data-testid="ta-airline" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Vuelo</Label>
                                            <Input required value={newTa.flight_number} onChange={e => setNewTa({...newTa, flight_number: e.target.value.toUpperCase()})} placeholder="AD-201" data-testid="ta-flight-input" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Matricula</Label>
                                            <Input value={newTa.aircraft_registration} onChange={e => setNewTa({...newTa, aircraft_registration: e.target.value.toUpperCase()})} placeholder="TG-AAA" data-testid="ta-reg" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Tipo Aeronave</Label>
                                            <Input value={newTa.aircraft_type} onChange={e => setNewTa({...newTa, aircraft_type: e.target.value})} placeholder="ATR 72-600" data-testid="ta-type" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Gate</Label>
                                            <Input value={newTa.gate} onChange={e => setNewTa({...newTa, gate: e.target.value.toUpperCase()})} placeholder="B3" data-testid="ta-gate" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Stand</Label>
                                            <Input value={newTa.stand} onChange={e => setNewTa({...newTa, stand: e.target.value})} placeholder="12" data-testid="ta-stand" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Hora Llegada</Label>
                                            <Input type="datetime-local" required value={newTa.arrival_time} onChange={e => setNewTa({...newTa, arrival_time: e.target.value})} data-testid="ta-arrival" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Hora Salida</Label>
                                            <Input type="datetime-local" value={newTa.departure_time} onChange={e => setNewTa({...newTa, departure_time: e.target.value})} data-testid="ta-departure" />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Notas</Label>
                                        <Textarea value={newTa.notes} onChange={e => setNewTa({...newTa, notes: e.target.value})} rows={2} data-testid="ta-notes" />
                                    </div>
                                    <Button type="submit" className="w-full btn-primary" data-testid="create-ta-submit">Crear Turnaround</Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="kpi-total">{kpis.total_today || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Hoy</p>
                </CardContent></Card>
                <Card className="card-aviation border-amber-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-amber-500" data-testid="kpi-progress">{kpis.in_progress || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">En Progreso</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="kpi-pending">{kpis.pending || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Pendientes</p>
                </CardContent></Card>
                <Card className="card-aviation border-emerald-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-emerald-500" data-testid="kpi-completed">{kpis.completed_today || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Completados</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="kpi-avg">{kpis.avg_turnaround_minutes || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Min Promedio</p>
                </CardContent></Card>
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="kpi-services">{kpis.services_completed || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Servicios</p>
                </CardContent></Card>
            </div>

            {/* Leaders Panel (Supervisor only) */}
            {isSupervisor && leaders.length > 0 && (
                <Card className="card-aviation">
                    <CardHeader className="py-3 px-4"><CardTitle className="text-sm font-['Barlow_Condensed'] uppercase">Lideres de Equipo</CardTitle></CardHeader>
                    <CardContent className="px-4 pb-3">
                        <div className="flex flex-wrap gap-2">
                            {leaders.map(l => {
                                const info = dashboard?.leaders?.find(x => x.id === l.id);
                                return (
                                    <Badge key={l.id} variant="outline" className="text-xs py-1 px-2" data-testid={`leader-${l.id}`}>
                                        <UserCheck className="w-3 h-3 mr-1" />
                                        {l.name}
                                        {info && <span className="ml-1 text-muted-foreground">({info.assigned_today} asignados)</span>}
                                    </Badge>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Turnarounds */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="active" data-testid="tab-active">
                        <CircleDot className="w-3.5 h-3.5 mr-1" />Activos ({activeTas.length})
                    </TabsTrigger>
                    <TabsTrigger value="completed" data-testid="tab-completed">
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />Completados ({completedTas.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="mt-4 space-y-4">
                    {activeTas.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                            <Plane className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p>No hay turnarounds activos</p>
                            {isSupervisor && <p className="text-xs mt-1">Crea uno nuevo para comenzar</p>}
                        </CardContent></Card>
                    ) : activeTas.map(t => (
                        <TurnaroundCard key={t.id} t={t} onRefresh={fetchAll} isLeader={!isSupervisor} leaders={leaders} />
                    ))}
                </TabsContent>

                <TabsContent value="completed" className="mt-4 space-y-4">
                    {completedTas.length === 0 ? (
                        <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
                            <p>No hay turnarounds completados</p>
                        </CardContent></Card>
                    ) : completedTas.map(t => (
                        <TurnaroundCard key={t.id} t={t} onRefresh={fetchAll} isLeader={!isSupervisor} leaders={leaders} />
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
